/*
 * Models for Topics and Topic Groups
 */

define([
  "backbone",
  "messaging/messenger",
  "configuration/properties",
  "jquery",
  "modules/topicsSplit/topic/topicModel",
  "modules/shared/sortableList/sortableListModel",
], function (Backbone, Messenger, Properties, $, TopicModel, SortableListModel) {
  let TopicsModel = {};

  TopicsModel.topicGroup = Backbone.Model.extend({
    idAttribute: "groupId",
    defaults: {
      name: "",
      description: "",
    },
    defaultValidateAttributes: ["name"],
    url: function () {
      const kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/intents/groups/" + this.id;
    },
    isRoot: function () {
      return this.get("groupId") === 0;
    },
    addSubGroup: function () {
      const newGroup = new TopicsModel.topicGroup();
      this.subGroups().add(newGroup);
      this.subGroups().sort();
      return newGroup;
    },
    addTopic: function () {
      //add a new topic to this group
      //create a new topic with the url set to be the href for getting the groups topics
      const groupsUrl = this.get("intents").href;
      const newTopic = new TopicModel.topic();
      newTopic.url = function () {
        return groupsUrl;
      };
      //add the new topic to the topicsCol
      this.topics().add(newTopic);
      return newTopic;
    },
    /**
     * Recursively descends the nav tree (as much as it has been loaded) and clears the
     * topics collection. This will cause all topic lists to be fetched fresh from the server the
     * next time they are needed.
     */
    clearTopicCollections: function () {
      delete this.topicsCol;
      if (this.subGroupsCol) {
        this.subGroupsCol.each(function (group) {
          group.clearTopicCollections();
        }, this);
      }
    },
    /**
     * Recursively descends the nav tree (as much as it has been loaded) and clears the
     * archived topics collection. This will cause all archived topic lists to be fetched fresh
     * from the server the next time they are needed.
     */
    clearArchivedTopicCollections: function () {
      delete this.archivedTopicsCol;
      if (this.subGroupsCol) {
        this.subGroupsCol.each(function (group) {
          group.clearArchivedTopicCollections();
        }, this);
      }
    },
    /**
     * Forces the topics collection to be fetched from the server, and clears any child topics
     * collections. By clearing the child collections, they will be fetched fresh from the
     * server the next time they are needed.
     */
    refreshTopicCollectionAndClearChildTopicCollections: function () {
      if (this.topicsCol) {
        this.topicsCol.fetch();
      }
      if (this.subGroupsCol) {
        this.subGroupsCol.each((group) => group.clearTopicCollections());
      }
    },
    /**
     * Forces the archived topics collection to be fetched from the server, and clears any child
     * archived topics collections. By clearing the child collections, they will be fetched fresh
     * from the server the next time they are needed.
     */
    refreshArchivedTopicCollectionAndClearChildArchivedTopicCollections: function () {
      if (this.archivedTopicsCol) {
        this.archivedTopicsCol.fetch();
      }
      if (this.subGroupsCol) {
        this.subGroupsCol.each((group) => group.clearArchivedTopicCollections());
      }
    },
    topics: function () {
      //load topics models
      if (!this.topicsCol) {
        this.topicsCol = new TopicsModel.topics();
        //attach a reference to the topic group to the collection
        this.topicsCol.topicGroup = this;
        //if the group exists on the server, load the entries from the server
        if (!this.isNew()) {
          this.topicsCol.url = this.get("intents").href;
          this.topicsCol.fetch();
          //don't bother sorting topics because the sortable list will deal with that for us
        }
      }
      return this.topicsCol;
    },
    archivedTopics: function () {
      if (!this.archivedTopicsCol) {
        this.archivedTopicsCol = new TopicsModel.topics();
        //attach a reference to the topic group to the collection
        this.archivedTopicsCol.topicGroup = this;
        //if the group exists on the server, load the entries from the server
        if (!this.isNew()) {
          if (this.get("archivedIntents")) {
            this.archivedTopicsCol.url = this.get("archivedIntents").href;
          }
          this.archivedTopicsCol.fetch();
          //don't bother sorting topics because the sortable list will deal with that for us
        }
      }
      return this.archivedTopicsCol;
    },
    updateArchivedTopics: function (archivedTopics) {
      archivedTopics.forEach((topic) => {
        if (this.topicsCol) {
          this.topicsCol.remove(topic);
          if (!this.archivedTopicsCol) {
            this.archivedTopicsCol = new TopicsModel.topics();
          }
          this.archivedTopicsCol.add(topic);
        }
      });
    },
    updateRestoredTopics: function (restoredTopics) {
      restoredTopics.forEach((topic) => {
        if (this.archivedTopicsCol) {
          this.archivedTopicsCol.remove(topic);
          if (!this.topicsCol) {
            this.topicsCol = new TopicsModel.topics();
          }
          this.topicsCol.add(topic);
        }
      });
    },
    findAlreadyLoadedTopics: function (intentId) {
      if (this.topicsCol) {
        let topicFound = this.topicsCol.findWhere({ intentId: intentId });
        if (topicFound) return topicFound;
      }
      if (this.subGroupsCol) {
        let topicFound;
        for (let i = 0; i < this.subGroupsCol.length; i++) {
          topicFound = this.subGroupsCol.at(i).findAlreadyLoadedTopics(intentId);
          if (topicFound) return topicFound;
        }
      }
    },
    //move an array of topics from the current folder to the destination folder
    move: function (topicsToMove, destinationGroup) {
      const length = topicsToMove.length;
      for (let i = 0; i < length; i++) {
        const topic = topicsToMove[i];
        //remove the topic from the group's collection, if it exists
        if (this.topicsCol) {
          this.topicsCol.remove(topic);
        }
        //move each topic (sends an update to the server)
        topic.move(destinationGroup);

        //now if the destination folder has its intents loaded already, make sure to move the
        //topics to that collection
        if (destinationGroup.topicsCol) {
          destinationGroup.topicsCol.add(topic);
        }
      }
    },
    //move this folder to another folder.  Assumes that this folder has already been removed from whatever
    //collection it was part of
    moveFolder: function (destinationGroup) {
      this.set("parentId", destinationGroup.get("groupId"));
      //when the destination group's sub groups are synced, add this group
      destinationGroup.subGroups().onSynced(function () {
        destinationGroup.subGroups().add(this);
        //make sure that there is no naming conflict in the new collection
        //add a (1),(2),(3)... to the end of the name if there is a conflict
        let value = this.get("name");
        const originalValue = value;
        let number = 1;
        while (this.validateAttribute("name", value)) {
          value = originalValue + "(" + number + ")";
          number++;
        }
        this.set("name", value, { silent: true });
        this.trigger("enter"); //trigger enter event as if the name change was made by a textbox
        destinationGroup.trigger("expand"); //make sure we see the moved folder in the parent folder
        this.trigger("showSelect"); //make sure the current folder is still selected
      }, this);
    },
    parse: function (response) {
      //if the group is the top level group, then replace the name of the group with the i18n'ed version
      if (response.groupId === 0) {
        response.name = $.t("topics.title");
      }
      return response;
    },
    saveChildren: function (options) {
      //set the parentId of the children
      if (this.subGroupsCol) {
        const parentId = this.get("groupId");
        const url = this.get("subgroups").href;
        this.subGroupsCol.each(function (group) {
          group.set("parentId", parentId);
          group.url = url; //pass url to children
        });
      }
      this.saveSubModels(options);
    },
    subGroups: function () {
      //if the group has sub groups, then fetch from the server.  Otherwise, don't fetch.
      this.subGroupsCol = this.getSubModel(
        this.subGroupsCol,
        TopicsModel.topicGroups,
        "/subgroups",
        true,
        !this.get("hasSubgroups"),
      );
      //pass the parent object to the collection for reference
      this.subGroupsCol.parent = this;
      //add events to determine when to change the hasSubgroups attribute
      //if group has subfolders, set hasSubGroups to true (this may be sent as false from the server if the subgroup hasn't been saved yet)
      this.subGroupsCol.on(
        "add remove sync",
        function () {
          const wasDirty = this.dirty;
          this.set("hasSubgroups", this.subGroupsCol.length > 0);
          this.dirty = wasDirty; //do not let the set event change the dirty flag because changing hasSubgroups should not require a save (but we do need to fire a change event)
        },
        this,
      );
      return this.subGroupsCol;
    },
    _validateDuplicateGroupName: function (value) {
      if (this.collection) {
        for (let i = 0; i < this.collection.length; i++) {
          const model = this.collection.at(i);
          if (model !== this) {
            if (value.toUpperCase() === model.get("name").toUpperCase()) {
              return $.t("topics.controls.duplicateValidationError");
            }
          }
        }
      }
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "name") {
        return (
          this.validateRequiredValue(value, { maxLength: 255 }) ||
          this.validateSafeCharacters(value) ||
          this._validateDuplicateGroupName(value)
        );
      }
    },
  });

  TopicsModel.topicGroups = Backbone.Collection.extend({
    model: TopicsModel.topicGroup,
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "name");
    },
  });

  TopicsModel.topics = Backbone.Collection.extend({
    model: TopicModel.topic,
  });

  TopicsModel.decisionTopics = Backbone.Collection.extend({
    model: TopicModel.decisionTopic,
    initializeCustom: function () {
      this.fetch();
    },
    url: function () {
      const kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/intents/decisions";
    },
  });

  //Request to load the top level Topic Group
  Messenger.reqres.setHandler("root:topic:group", function () {
    const group = new TopicsModel.topicGroup({ groupId: 0 });
    group.fetch();
    return group;
  });

  //Request to load a Topic Group
  Messenger.reqres.setHandler("topic:group", function (groupId) {
    const group = new TopicsModel.topicGroup({ groupId: groupId });
    group.fetch();
    return group;
  });

  //Request for the headers of the topics list
  Messenger.reqres.setHandler("topics:headers", function () {
    const Header = SortableListModel.headerModel;
    return new Backbone.Collection([
      new Header({ checkbox: true }),
      new Header({
        headerName: $.t("topics.headers.name"),
        modelAttribute: "intentName",
        selectable: true,
        sortable: true,
        filterable: true,
        longTextField: true,
        droppable: true,
        width: "25%",
      }),
      new Header({
        headerName: $.t("topics.headers.description"),
        modelAttribute: "intentDescription",
        sortable: true,
        filterable: true,
        longTextField: true,
        hideable: true,
        width: "35%",
      }),
      new Header({
        headerName: $.t("topics.headers.status"),
        modelAttribute: "_thisObject.statusValue",
        sortable: true,
        filterable: true,
        textField: true,
        width: "22%",
      }),
      new Header({
        headerName: $.t("topics.headers.context"),
        modelAttribute: "_thisObject.contextValue",
        sortable: true,
        filterable: true,
        textField: true,
        hideable: true,
        width: "18%",
      }),
    ]);
  });

  return TopicsModel;
});
